import { Injectable } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { catchError, filter, finalize, first, shareReplay, switchMap } from 'rxjs/operators';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { Router } from '@angular/router';

import { ILoggedInUserViewModel } from '../../model/common.model';
import { EMPTY_GUID_VALUE } from '@properties/application.properties';
import { LocalStorageService } from '@services/local-storage.service';
import { B2cService } from './b2c.service';
import { B2CResponseInterface } from '../interfaces/b2c-response.interface';
import { environment } from '@environments/environment';

const JSONP_REQUEST_METHOD = 'JSONP';

/**
 * implementation of http interceptor
 */
@Injectable()
export class HttpInterceptorService implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

  constructor(
    private readonly localStorageService: LocalStorageService,
    private readonly b2cService: B2cService,
    private readonly router: Router,
  ) {}

  /**
   * http interceptor for application
   * @param request - http request
   * @param next - http handler
   */
  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Angular's JSONP backend rejects any request that carries headers, so third-party script loads
    // (e.g. the Google Maps API) must pass through untouched.
    if (request.method === JSONP_REQUEST_METHOD) {
      return next.handle(request);
    }

    // requests to B2C itself (login / token refresh) must not carry the bearer token nor trigger a refresh
    if (request.url.includes(environment.authority)) {
      return next.handle(request);
    }

    const accessToken = this.localStorageService.getAccessToken();
    if (!accessToken) {
      this.localStorageService.onLogout();
      return next.handle(request);
    }

    // proactive refresh: token is expired or about to expire, refresh before sending the request
    if (this.localStorageService.isAccessTokenExpiringSoon()) {
      return this.refreshAndProceed(request, next);
    }

    return next.handle(this.setHeaders(request, accessToken)).pipe(
      catchError((response: HttpErrorResponse) => {
        if (response.status === 401) {
          // reactive fallback (e.g. client/server clock skew): refresh and retry
          return this.refreshAndProceed(request, next);
        }

        return throwError(() => response);
      }),
    );
  }

  /**
   * set required headers for requests
   * @param request - http request
   * @param accessToken - access token
   */
  private setHeaders(request: HttpRequest<any>, accessToken: string): HttpRequest<any> {
    const userDetails: ILoggedInUserViewModel = this.localStorageService.getCurrentUserDetails();

    const headers = {
      setHeaders: {
        Authorization: 'Bearer ' + accessToken,
        RoleId: userDetails && userDetails.roleId ? userDetails.roleId : EMPTY_GUID_VALUE,
        BusinessUnitId: userDetails?.businessUnitId ? userDetails?.businessUnitId : EMPTY_GUID_VALUE,
      },
    };

    return request.clone(headers);
  }

  /**
   * refresh the access token and proceed with the request (used proactively and as 401 fallback)
   * @param request - http request
   * @param next - http handler
   */
  private refreshAndProceed(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (this.isRefreshing) {
      return this.awaitOngoingRefresh(request, next);
    }

    return this.performTokenRefresh(request, next);
  }

  /**
   * trigger a token refresh, then retry the request with the new token
   * @param request - http request
   * @param next - http handler
   */
  private performTokenRefresh(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    this.isRefreshing = true;
    this.refreshTokenSubject.next(null);
    const refreshToken = this.localStorageService.getRefreshToken();

    if (!refreshToken) {
      this.localStorageService.onLogout();
      return throwError(() => new Error('No refresh token available'));
    }

    return this.b2cService.getB2CRefreshTokenData(refreshToken).pipe(
      first(),
      switchMap((tokenDetails: B2CResponseInterface) => {
        this.isRefreshing = false;
        this.localStorageService.setTokens(tokenDetails);
        this.refreshTokenSubject.next(tokenDetails.access_token);

        return next.handle(this.setHeaders(request, tokenDetails.access_token));
      }),
      catchError((error: HttpErrorResponse) => {
        this.localStorageService.onLogout();
        this.refreshTokenSubject.next(null);

        if (error.status === 400) {
          void this.router.navigate(['/login']);
          return throwError(() => new Error('Refresh token invalid or expired'));
        }

        return throwError(() => error);
      }),
      finalize(() => (this.isRefreshing = false)),
    );
  }

  /**
   * wait for the in-flight refresh to finish, then retry the request with the new token
   * @param request - http request
   * @param next - http handler
   */
  private awaitOngoingRefresh(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return this.refreshTokenSubject.pipe(
      filter((token) => !!token),
      first(),
      switchMap((token) => next.handle(this.setHeaders(request, token))),
      shareReplay(1),
    );
  }
}
